import { ASSET_OPTIONS, AccountType } from '@client/util/constants';
import {
  calcCapitalGainsTaxLiability,
  calcFutureValue,
  calcFutureValueOfMonthlyContributions,
} from '@shared';
import { isLifeInsuranceQuote, isTraditionalLTCQuote } from './quoteTypeGuards';

export function calcNetFutureValueForAccount(
  detail: SelfFundingSource | LtcFinancialPlanningAccountDetail,
  yearsTillLtc: number,
  contributionMonthCount: number,
): number {
  // Handle both SelfFundingSource and LtcFinancialPlanningAccountDetail types
  const annualRateOfReturn =
    'annualRateOfReturn' in detail ? detail.annualRateOfReturn || 0 : detail.expectedReturn || 0;
  const gainsTaxRate =
    'gainsTaxRate' in detail ? detail.gainsTaxRate || 0 : detail.capitalGainsTaxRate || 0;
  const monthlyContributionAmount =
    'monthlyContributionAmount' in detail
      ? detail.monthlyContributionAmount || 0
      : detail.futureContributions || 0;
  const existingAssetsValue =
    'existingAssetsValue' in detail ? detail.existingAssetsValue || 0 : detail.currentValue || 0;

  const futureValueOfInitialInvestment = calcFutureValue(
    existingAssetsValue,
    annualRateOfReturn,
    yearsTillLtc,
  );

  const taxOnInitialInvestment = calcCapitalGainsTaxLiability(
    existingAssetsValue,
    gainsTaxRate,
    futureValueOfInitialInvestment,
  );

  const netFutureValueOfInitialInvestment = futureValueOfInitialInvestment - taxOnInitialInvestment;

  const futureValueOfContributions = calcFutureValueOfMonthlyContributions(
    monthlyContributionAmount,
    annualRateOfReturn,
    contributionMonthCount,
  );

  const totalContributed = monthlyContributionAmount * contributionMonthCount;
  const taxOnContributions = calcCapitalGainsTaxLiability(
    totalContributed,
    gainsTaxRate,
    futureValueOfContributions,
  );

  const netFutureValueOfContributions = futureValueOfContributions - taxOnContributions;

  return netFutureValueOfInitialInvestment + netFutureValueOfContributions;
}

function getAdjustedAccountDetail(
  detail: LtcFinancialPlanningAccountDetail,
  allocation: number,
): LtcFinancialPlanningAccountDetail {
  return {
    ...detail,
    currentValue: Math.max(0, detail.currentValue - allocation),
  };
}

/**
 * Calculates the actual payment period in months for a given payment option.
 * For 10-Pay, returns the minimum of 10 years or years until LTC (since premiums stop at claim).
 * For Monthly/Annually, returns the full contribution period until LTC.
 */
export function calculatePaymentPeriodMonths(
  paymentOption: LtcFinancialPlanningPaymentOption,
  contributionMonthCount: number,
): number {
  const yearsUntilLtc = contributionMonthCount / 12;

  switch (paymentOption) {
    case '5-Pay':
      return Math.min(5, yearsUntilLtc) * 12;
    case '10-Pay':
      // Pay for 10 years OR until LTC event, whichever comes first
      return Math.min(10, yearsUntilLtc) * 12;
    case 'Monthly':
    case 'Annually':
      // Pay until LTC event (premiums stop at claim)
      return contributionMonthCount;
    default:
      return 0;
  }
}

export function calculateTotalContribution(allocation: number): number {
  return allocation;
}

export function getPaymentOption(
  policy: PolicyQuote | TraditionalLTCQuote | null,
): LtcFinancialPlanningPaymentOption {
  if (!policy) return 'Single Pay';

  let source = '';

  if (isTraditionalLTCQuote(policy)) {
    source = policy.premiumFrequency.toLowerCase();
  } else if (isLifeInsuranceQuote(policy)) {
    source =
      policy.cobPaymentOption?.toLowerCase() || policy.productFundingOption?.toLowerCase() || '';
  }

  if (/\b5\s*[- ]?(?:pay|years?)\b/.test(source) || source.includes('five pay')) return '5-Pay';
  if (source.includes('10') || source.includes('ten')) return '10-Pay';
  if (source.includes('monthly')) return 'Monthly';
  if (source.includes('annual')) return 'Annually';

  return 'Single Pay';
}

export function calculatePolicyPremium(
  policy: PolicyQuote | TraditionalLTCQuote | null,
  contributionMonthCount: number,
): number {
  if (!policy) return 0;

  const paymentOption = getPaymentOption(policy);

  let premiumAmount: number;

  if (isTraditionalLTCQuote(policy)) {
    premiumAmount = policy.primaryModalPremium;
  } else if (isLifeInsuranceQuote(policy)) {
    premiumAmount = policy.premiumAmount;
  } else {
    return 0;
  }

  if (paymentOption === 'Single Pay') {
    return premiumAmount;
  }

  const paymentPeriodMonths = calculatePaymentPeriodMonths(paymentOption, contributionMonthCount);
  return (premiumAmount * paymentPeriodMonths) / 12;
}

export function calcSelfFundingCapacity(
  selectedAssets: AccountType[],
  accountDetails: Partial<Record<string, LtcFinancialPlanningAccountDetail>>,
  yearsTillLtc: number,
  contributionMonthCount: number,
): number {
  return selectedAssets.reduce((sum, accountType) => {
    const detail = accountDetails[accountType];
    if (!detail) return sum;
    return sum + calcNetFutureValueForAccount(detail, yearsTillLtc, contributionMonthCount);
  }, 0);
}

/**
 * Premium deduction details for a single self-funding source
 */
export interface PremiumDeduction {
  fundingSourceId: string;
  originalValue: number;
  newValue: number;
  deduction: number;
}

/**
 * Calculates how to deduct insurance premium costs from self-funding sources.
 * Deducts from sources with lowest ROI first to preserve higher-return assets.
 *
 * @param multipleFundingSources - The client's funding sources
 * @param totalPolicyCost - Total cost of insurance premiums to deduct
 * @returns Array of deductions to apply to each affected source
 */
export function calculatePremiumDeductions(
  multipleFundingSources: MultipleFundingSources | undefined,
  totalPolicyCost: number,
): PremiumDeduction[] {
  if (!multipleFundingSources?.selfFunding || totalPolicyCost <= 0) {
    return [];
  }

  const selfFundingSources = multipleFundingSources.selfFunding;
  const deductions: PremiumDeduction[] = [];

  // Sort self-funding sources by annualRateOfReturn (ascending - lowest ROI first)
  const sortedSources = Object.entries(selfFundingSources)
    .filter(([_, source]) => !source.isDeactivated && source.existingAssetsValue)
    .sort(([_, a], [__, b]) => {
      const roiA = a.annualRateOfReturn || 0;
      const roiB = b.annualRateOfReturn || 0;
      return roiA - roiB;
    });

  // Deduct policy cost from existingAssetsValue
  let remainingCost = totalPolicyCost;

  for (const [fundingSourceId, source] of sortedSources) {
    if (remainingCost <= 0) break;

    const existingValue = source.existingAssetsValue || 0;
    const deduction = Math.min(existingValue, remainingCost);

    if (deduction > 0) {
      deductions.push({
        fundingSourceId,
        originalValue: existingValue,
        newValue: existingValue - deduction,
        deduction,
      });

      remainingCost -= deduction;
    }
  }

  // Log warning if we couldn't fully deduct the cost
  if (remainingCost > 0) {
    console.warn(
      `Policy cost (${totalPolicyCost}) exceeds available self-funding assets. Remaining: ${remainingCost}`,
    );
  }

  return deductions;
}
